/**
 * Version/runtime consistency helpers
 *
 * Context:
 * - Static chunks under `/assets/*` are content-hashed and served via S3/CloudFront with
 *   `Cache-Control: public, max-age=31536000, immutable`.
 * - `index.html` and `/version.json` are uploaded with `Cache-Control: no-store` and
 *   invalidated on deploy so the shell can discover new releases.
 * - S3 keeps old hashed assets for 60 days via a lifecycle rule, so users on an older
 *   shell can still fetch the matching chunks during the rollout window.
 *
 * How it works:
 * - We inject the current git SHA both as a meta tag (`<meta name="git-sha" ...>`) and
 *   as a global `__GIT_SHA__` at build time. We also emit `/version.json` with `{ sha }`.
 * - `checkForUpdate()` fetches `/version.json` with `no-store` and compares SHAs.
 * - `installVersionChecks()` runs this on tab visibility and periodically; on mismatch
 *   we reload to align the shell with the latest chunk map and backend schema.
 * - `reloadOnceForChunkError()` force-reloads if a lazy chunk fails to load (e.g. a
 *   404 on a removed file) as a last-resort self-heal. It is wired both to the window
 *   handlers in `installChunkLoadReload()` and to the app's error boundary, because a
 *   lazy route's failure is caught by React and never reaches the window.
 */
const meta = document.querySelector('meta[name="git-sha"]') as HTMLMetaElement | null;
export const CURRENT_GIT_SHA: string | undefined =
  meta?.content || (typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : undefined);

/**
 * Fetch `/version.json` (uncached) and report whether a newer version exists.
 */
export async function checkForUpdate(): Promise<boolean> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) {
      return false;
    }
    const data = (await res.json()) as { sha?: string };
    if (!data?.sha || !CURRENT_GIT_SHA) {
      return false;
    }
    return data.sha !== CURRENT_GIT_SHA;
  } catch {
    return false;
  }
}

/**
 * Install lightweight version polling. On change, invoke `onUpdate` (typically reload).
 */
export function installVersionChecks(onUpdate: () => void) {
  const run = async () => {
    if (await checkForUpdate()) {
      onUpdate();
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void run();
    }
  });

  // Periodic check
  window.setInterval(
    () => {
      void run();
    },
    5 * 60 * 1000,
  );
}

/**
 * A stale shell fails to load an asset in several different wordings depending on the browser and
 * on whether the asset was script or stylesheet, and matching only the first two let the stylesheet
 * case reach the error page untouched.
 */
const CHUNK_LOAD_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'ChunkLoadError',
  // Vite's CSS preload helper, which fails the same way a dynamic import does.
  'Unable to preload CSS for',
  // Safari's wording for the same failed module fetch.
  'Importing a module script failed',
];

/** Exported for testing, and for the error boundary to classify what it just caught. */
export function isChunkLoadError(message: string): boolean {
  const msg = message || '';

  return CHUNK_LOAD_ERROR_MESSAGES.some((candidate) => msg.includes(candidate));
}

const CHUNK_RELOAD_GUARD_KEY = 'altitude:chunk-reload-attempted';

/** How long the app has to survive before we trust it again and re-arm the one-shot reload. */
export const CHUNK_RELOAD_GUARD_CLEAR_MS = 10_000;

/**
 * Reload once to pick up the current chunk map after a stale shell fails to fetch an asset.
 *
 * Why once: the reload only helps when the asset is missing because this tab is out of date. If it
 * is missing for any other reason it will still be missing afterwards, and reloading on every
 * failure would spin the page forever. The first attempt records itself, and a second identical
 * failure is allowed through to the error page instead. Session storage being unavailable makes the
 * two indistinguishable, so we leave the page alone rather than risk the loop.
 *
 * `reload` is injectable so the guard can be tested without navigating.
 */
export function reloadOnceForChunkError(message: string, reload = () => window.location.reload()): boolean {
  if (!isChunkLoadError(message)) {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    return false;
  }

  reload();

  return true;
}

/** Re-arm the one-shot reload, so a later deploy can still self-heal this tab. */
export function clearChunkReloadGuard(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
  } catch {
    // No storage means no guard was ever set.
  }
}

/**
 * Auto-reload if a dynamic import fails. This recovers from transient races and from
 * users holding an old shell that references chunks which may have been removed.
 *
 * These listeners only see failures that escape React. A lazy route's failure is caught by the
 * error boundary around the app and never reaches them, so the boundary calls
 * `reloadOnceForChunkError` itself.
 */
export function installChunkLoadReload() {
  window.addEventListener('error', (e: ErrorEvent) => {
    if (e?.message) {
      reloadOnceForChunkError(e.message);
    }
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e && (e as { reason: unknown }).reason;
    const message = typeof reason === 'string' ? reason : (reason as { message: unknown })?.message;
    if (message) {
      reloadOnceForChunkError(String(message));
    }
  });
}
